/**
 * Silhouette Outline Pass — post-processing edge detection for selected objects.
 * 
 * Wraps Three.js OutlinePass with the geoviewer render pipeline to replace
 * the box-wireframe selection visualization with true silhouette outlines.
 * 
 * Features:
 * - Edge color: #00ccff (brand color)  
 * - Edge thickness: 2px
 * - Gentle 0.5Hz pulse animation (sine wave modulation of edge strength)
 * - Multi-select support (set of selected objects)
 */

import * as THREE from 'three';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';

export interface OutlinePassManager {
  /**
   * Set the objects to be outlined. Pass empty array to clear selection.
   */
  setSelectedObjects(objects: THREE.Object3D[]): void;
  
  /**
   * Update the outline pass (call in render loop for animation).
   */
  update(deltaTime: number): void;
  
  /**
   * Render the scene with outline pass.
   */
  render(): void;
  
  /**
   * Dispose resources.
   */
  dispose(): void;
  
  /**
   * Resize the outline pass when viewport changes.
   */
  setSize(width: number, height: number): void;
}

export interface OutlinePassConfig {
  scene: THREE.Scene;
  camera: THREE.Camera;
  renderer: THREE.WebGLRenderer;
  edgeColor?: number;
  edgeThickness?: number;
  pulseFrequency?: number;
  minEdgeStrength?: number;
  maxEdgeStrength?: number;
}

export function createOutlinePassManager(config: OutlinePassConfig): OutlinePassManager {
  const {
    scene,
    camera,
    renderer,
    edgeColor = 0x00ccff,
    edgeThickness = 2,
    pulseFrequency = 0.5,
    minEdgeStrength = 1.0,
    maxEdgeStrength = 3.0,
  } = config;

  // Create effect composer with render pass and outline pass
  const composer = new EffectComposer(renderer);
  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  const outlinePass = new OutlinePass(
    new THREE.Vector2(renderer.domElement.width, renderer.domElement.height),
    scene,
    camera
  );
  
  // Configure outline appearance
  outlinePass.edgeStrength = minEdgeStrength;
  outlinePass.edgeGlow = 0.0;
  outlinePass.edgeThickness = edgeThickness;
  outlinePass.pulsePeriod = 0;
  outlinePass.visibleEdgeColor.setHex(edgeColor);
  outlinePass.hiddenEdgeColor.setHex(edgeColor);

  composer.addPass(outlinePass);

  let animationTime = 0;

  return {
    setSelectedObjects(objects: THREE.Object3D[]): void {
      outlinePass.selectedObjects = objects;
    },

    update(deltaTime: number): void {
      animationTime += deltaTime;
      
      // Pulse animation: sine wave modulation of edge strength
      const pulsePhase = animationTime * pulseFrequency * 2 * Math.PI;
      const pulseValue = Math.sin(pulsePhase);
      const normalizedPulse = (pulseValue + 1) / 2; // Convert from [-1,1] to [0,1]
      
      outlinePass.edgeStrength = minEdgeStrength + normalizedPulse * (maxEdgeStrength - minEdgeStrength);
    },

    render(): void {
      composer.render();
    },

    dispose(): void {
      composer.dispose();
      outlinePass.dispose();
      renderPass.dispose();
    },

    setSize(width: number, height: number): void {
      composer.setSize(width, height);
      outlinePass.setSize(width, height);
    },
  };
}

/**
 * Performance monitoring utility for outline pass.
 * Returns average frame time over the last N frames.
 */
export function createPerformanceMonitor(sampleSize = 60) {
  const frameTimes: number[] = [];
  let lastFrameTime = performance.now();

  return {
    recordFrame(): void {
      const now = performance.now();
      const frameTime = now - lastFrameTime;
      lastFrameTime = now;

      frameTimes.push(frameTime);
      if (frameTimes.length > sampleSize) {
        frameTimes.shift();
      }
    },

    getAverageFrameTime(): number {
      if (frameTimes.length === 0) return 0;
      return frameTimes.reduce((sum, time) => sum + time, 0) / frameTimes.length;
    },

    getMaxFrameTime(): number {
      return frameTimes.length > 0 ? Math.max(...frameTimes) : 0;
    },

    isPerformanceAcceptable(maxFrameTimeMs = 16.67): boolean {
      return this.getAverageFrameTime() <= maxFrameTimeMs;
    },
  };
}